import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { TimesheetApprovalEntry, TimesheetApprovalSummaryRow } from '@catalyst-psa/shared';

import {
  useApproveTimesheetByToken,
  useRejectTimesheetByToken,
  useTimesheetApprovalView,
} from './api.js';

/** Active summary filter applied to the grid + entry list (mirrors the timesheet page). */
type ApprovalFilter =
  | { kind: 'billable'; value: 'billable' | 'non-billable' }
  | { kind: 'customer'; value: string }
  | { kind: 'chargeCode'; value: string }
  | { kind: 'serviceCategory'; value: string }
  | { kind: 'billingClass'; value: string };

const FILTER_KIND_LABELS: Record<ApprovalFilter['kind'], string> = {
  billable: 'Billable',
  customer: 'Customer',
  chargeCode: 'Charge code',
  serviceCategory: 'Service category',
  billingClass: 'Billing class',
};

function describeFilter(filter: ApprovalFilter): string {
  if (filter.kind === 'billable') {
    return filter.value === 'billable' ? 'Billable hours' : 'Non-billable hours';
  }
  return `${FILTER_KIND_LABELS[filter.kind]}: ${filter.value}`;
}

function entryMatchesFilter(entry: TimesheetApprovalEntry, filter: ApprovalFilter): boolean {
  switch (filter.kind) {
    case 'billable':
      return filter.value === 'billable' ? entry.billable : !entry.billable;
    case 'customer':
      return entry.customerName === filter.value;
    case 'chargeCode':
      return entry.chargeCodeLabel === filter.value;
    case 'serviceCategory':
      return entry.serviceCategoryName === filter.value;
    case 'billingClass':
      return entry.billingClassName === filter.value;
  }
}

function fmt(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(Number(n.toFixed(2))) : value;
}

function addDaysIso(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function weekLabel(iso: string): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function dayHeader(iso: string): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  });
}

function SummaryBox({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
        {value}
        {sub ? <span className="text-base font-normal text-slate-400"> {sub}</span> : null}
      </p>
    </div>
  );
}

function Breakdown({
  title,
  rows,
  onSelect,
  activeValue,
}: {
  title: string;
  rows: TimesheetApprovalSummaryRow[];
  /** When provided, each row becomes a clickable filter button. */
  onSelect?: (label: string) => void;
  /** The currently-active value for this breakdown (highlights the row). */
  activeValue?: string | null;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-slate-400">—</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {rows.map((r) => {
            const isActive = activeValue === r.label;
            const content = (
              <>
                <span className="truncate text-left text-slate-700">{r.label}</span>
                <span className="shrink-0 font-medium tabular-nums text-slate-900">
                  {fmt(r.hours)}
                </span>
              </>
            );
            return (
              <li key={r.label}>
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(r.label)}
                    className={`flex w-full items-baseline justify-between gap-3 rounded px-1 text-sm hover:bg-brand-50 ${
                      isActive ? 'bg-brand-50 ring-1 ring-brand-300' : ''
                    }`}
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex items-baseline justify-between gap-3 text-sm">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function WeeklyGrid({
  weekStartDate,
  entries,
}: {
  weekStartDate: string;
  entries: TimesheetApprovalEntry[];
}): JSX.Element | null {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysIso(weekStartDate, i)),
    [weekStartDate],
  );
  const rows = useMemo(() => {
    const map = new Map<string, { label: string; customer: string; byDay: Map<string, number> }>();
    for (const e of entries) {
      const key = `${e.customerName}||${e.workLabel}`;
      let row = map.get(key);
      if (!row) {
        row = { label: e.workLabel, customer: e.customerName, byDay: new Map() };
        map.set(key, row);
      }
      row.byDay.set(e.workDate, (row.byDay.get(e.workDate) ?? 0) + Number(e.hours));
    }
    return [...map.values()];
  }, [entries]);
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[680px] text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3 font-medium">Work item</th>
            {days.map((d) => (
              <th key={d} className="px-2 py-3 text-center font-medium">
                {dayHeader(d)}
              </th>
            ))}
            <th className="px-3 py-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            let total = 0;
            return (
              <tr key={`${row.customer}|${row.label}`}>
                <td className="px-4 py-3">
                  {row.customer ? (
                    <span className="font-medium text-slate-900">{row.customer} </span>
                  ) : null}
                  <span className="text-slate-700">{row.label}</span>
                </td>
                {days.map((d) => {
                  const v = row.byDay.get(d) ?? 0;
                  total += v;
                  return (
                    <td key={d} className="px-2 py-3 text-center tabular-nums text-slate-700">
                      {v === 0 ? '' : Number(v.toFixed(2))}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right font-medium tabular-nums text-slate-900">
                  {Number(total.toFixed(2))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EntryList({ entries }: { entries: TimesheetApprovalEntry[] }): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3 font-medium">Date</th>
            <th className="px-4 py-3 font-medium">Customer</th>
            <th className="px-4 py-3 font-medium">Work item</th>
            <th className="px-4 py-3 font-medium">Service category</th>
            <th className="px-4 py-3 font-medium">Billing class</th>
            <th className="px-4 py-3 text-right font-medium">Hours</th>
            <th className="px-4 py-3 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entries.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                No time entries.
              </td>
            </tr>
          ) : (
            entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-3 text-slate-600">{e.workDate}</td>
                <td className="px-4 py-3 text-slate-600">{e.customerName}</td>
                <td className="px-4 py-3 text-slate-900">{e.workLabel}</td>
                <td className="px-4 py-3 text-slate-600">{e.serviceCategoryName}</td>
                <td className="px-4 py-3 text-slate-600">
                  {e.billingClassName}
                  {e.billable ? '' : <span className="text-slate-400"> (non-billable)</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-900">{fmt(e.hours)}</td>
                <td className="px-4 py-3 text-slate-600">{e.notes ?? ''}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function PublicTimesheetApprovalPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useTimesheetApprovalView(token);
  const approveMutation = useApproveTimesheetByToken(token);
  const rejectMutation = useRejectTimesheetByToken(token);

  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<null | 'approved' | 'advanced' | 'rejected'>(null);
  const [filter, setFilter] = useState<ApprovalFilter | null>(null);

  /** Applies a summary filter, or clears it when the same one is clicked again. */
  const toggleFilter = useCallback((next: ApprovalFilter) => {
    setFilter((current) =>
      current && current.kind === next.kind && current.value === next.value ? null : next,
    );
  }, []);

  const allEntries = viewQuery.data?.model.entries;
  const filteredEntries = useMemo(() => {
    const entries = allEntries ?? [];
    return filter ? entries.filter((e) => entryMatchesFilter(e, filter)) : entries;
  }, [allEntries, filter]);

  // Every summary number reflects the ACTIVE FILTER (mirrors the main timesheet page):
  // clicking a tile recalculates the whole page. With no filter these equal the
  // server-computed totals.
  const breakdowns = useMemo(() => {
    let total = 0;
    let billableTotal = 0;
    const byCustomer = new Map<string, number>();
    const byChargeCode = new Map<string, number>();
    const byServiceCategory = new Map<string, number>();
    const byBillingClass = new Map<string, number>();
    const add = (map: Map<string, number>, label: string, hours: number): void => {
      map.set(label, (map.get(label) ?? 0) + hours);
    };
    for (const e of filteredEntries) {
      const hours = Number(e.hours);
      total += hours;
      if (e.billable) billableTotal += hours;
      if (hours === 0) continue;
      add(byCustomer, e.customerName, hours);
      if (e.chargeCodeLabel) add(byChargeCode, e.chargeCodeLabel, hours);
      add(byServiceCategory, e.serviceCategoryName, hours);
      add(byBillingClass, e.billingClassName, hours);
    }
    const toRows = (map: Map<string, number>): TimesheetApprovalSummaryRow[] =>
      [...map.entries()]
        .map(([label, hours]) => ({ label, hours: hours.toFixed(2) }))
        .sort((a, b) => Number(b.hours) - Number(a.hours) || a.label.localeCompare(b.label));
    return {
      total,
      billableTotal,
      nonBillableTotal: total - billableTotal,
      byCustomer: toRows(byCustomer),
      byChargeCode: toRows(byChargeCode),
      byServiceCategory: toRows(byServiceCategory),
      byBillingClass: toRows(byBillingClass),
    };
  }, [filteredEntries]);

  if (viewQuery.isLoading) {
    return <CenteredNote text="Loading timesheet…" />;
  }
  if (viewQuery.isError || !viewQuery.data) {
    return (
      <CenteredNote text="This approval link is not available. It may have expired or already been used." />
    );
  }

  const view = viewQuery.data;
  const { model } = view;
  const total = breakdowns.total;
  const billable = breakdowns.billableTotal;
  const percent = total > 0 ? Math.round((billable / total) * 100) : 0;
  const closed =
    done !== null ||
    view.decided ||
    view.timesheetStatus !== 'submitted' ||
    view.stepStatus !== 'pending';

  const outcomeMessage = (): string | null => {
    if (done === 'approved') return 'Approved. This timesheet is now fully approved. Thank you!';
    if (done === 'advanced')
      return 'Approved. The timesheet has been forwarded to the next approver. Thank you!';
    if (done === 'rejected')
      return 'This timesheet has been un-submitted and returned to the owner.';
    if (view.stepStatus === 'approved') return 'You have already approved this timesheet.';
    if (view.stepStatus === 'rejected') return 'You have already un-submitted this timesheet.';
    if (view.timesheetStatus === 'approved')
      return 'This timesheet has already been fully approved.';
    if (view.timesheetStatus === 'rejected') return 'This timesheet has already been un-submitted.';
    return null;
  };

  const handleApprove = (): void => {
    approveMutation.mutate(undefined, {
      onSuccess: (r) => setDone(r.advancedToNext ? 'advanced' : 'approved'),
    });
  };
  const handleReject = (): void => {
    if (!reason.trim()) return;
    rejectMutation.mutate(reason.trim(), { onSuccess: () => setDone('rejected') });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-5xl px-4">
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Timesheet approval
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            Week of {weekLabel(model.weekStartDate)}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {model.memberName} · Reviewer: {view.approverName}
          </p>
        </div>

        {outcomeMessage() ? (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {outcomeMessage()}
          </div>
        ) : null}

        {(approveMutation.isError || rejectMutation.isError) && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {(() => {
              const err = approveMutation.error ?? rejectMutation.error;
              return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
            })()}
          </div>
        )}

        <div className="mb-4 grid gap-4 sm:grid-cols-3">
          <SummaryBox label="Total hours" value={String(Number(total.toFixed(2)))} />
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Billable / Non-billable
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              <button
                type="button"
                onClick={() => toggleFilter({ kind: 'billable', value: 'billable' })}
                className={`rounded px-1 hover:bg-brand-50 ${
                  filter?.kind === 'billable' && filter.value === 'billable'
                    ? 'bg-brand-50 ring-1 ring-brand-300'
                    : ''
                }`}
              >
                {Number(billable.toFixed(2))}
              </button>
              <span className="text-base font-normal text-slate-400"> / </span>
              <button
                type="button"
                onClick={() => toggleFilter({ kind: 'billable', value: 'non-billable' })}
                className={`rounded px-1 text-base font-normal text-slate-400 hover:bg-brand-50 ${
                  filter?.kind === 'billable' && filter.value === 'non-billable'
                    ? 'bg-brand-50 ring-1 ring-brand-300'
                    : ''
                }`}
              >
                {Number(breakdowns.nonBillableTotal.toFixed(2))}
              </button>
            </p>
          </div>
          <SummaryBox label="Percent billable" value={`${percent}%`} />
        </div>

        <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Breakdown
            title="Hours by customer"
            rows={breakdowns.byCustomer}
            onSelect={(label) => toggleFilter({ kind: 'customer', value: label })}
            activeValue={filter?.kind === 'customer' ? filter.value : null}
          />
          <Breakdown
            title="Hours by charge code"
            rows={breakdowns.byChargeCode}
            onSelect={(label) => toggleFilter({ kind: 'chargeCode', value: label })}
            activeValue={filter?.kind === 'chargeCode' ? filter.value : null}
          />
          <Breakdown
            title="Hours by service category"
            rows={breakdowns.byServiceCategory}
            onSelect={(label) => toggleFilter({ kind: 'serviceCategory', value: label })}
            activeValue={filter?.kind === 'serviceCategory' ? filter.value : null}
          />
          <Breakdown
            title="Hours by billing class"
            rows={breakdowns.byBillingClass}
            onSelect={(label) => toggleFilter({ kind: 'billingClass', value: label })}
            activeValue={filter?.kind === 'billingClass' ? filter.value : null}
          />
        </div>

        {filter ? (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-800">
            <span>
              Filtered by <span className="font-semibold">{describeFilter(filter)}</span> ·{' '}
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
            </span>
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="rounded-md border border-brand-300 bg-white px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              Clear filter
            </button>
          </div>
        ) : null}

        {model.notes ? (
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Notes</p>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{model.notes}</p>
          </div>
        ) : null}

        <h2 className="mb-2 mt-6 text-sm font-semibold text-slate-700">Weekly grid</h2>
        <div className="mb-4">
          <WeeklyGrid weekStartDate={model.weekStartDate} entries={filteredEntries} />
        </div>

        <h2 className="mb-2 mt-6 text-sm font-semibold text-slate-700">Time entries</h2>
        <EntryList entries={filteredEntries} />

        {!closed ? (
          <div className="sticky bottom-0 mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
            {rejecting ? (
              <div className="flex flex-col gap-3">
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for un-submission (required)…"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRejecting(false)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={!reason.trim() || rejectMutation.isPending}
                    className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    {rejectMutation.isPending ? 'Un-submitting…' : 'Confirm un-submit'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setRejecting(true)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Un-Submit…
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {approveMutation.isPending ? 'Approving…' : 'Approve timesheet'}
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CenteredNote({ text }: { text: string }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600 shadow-sm">
        {text}
      </div>
    </div>
  );
}
